import {
  DxButton,
  DxCard,
  DxCardContent,
  DxDateInput,
  DxDropdown,
  DxGrid,
  DxListItem,
  DxModal,
  DxText,
  DxTextInput,
} from '@dvag/design-system-react';
import { appNames } from '@dvag/dfs-constant-config/app-names';
import { useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { mandatoryFieldMessage } from 'form/util';
import { validateEmail } from 'form/validation';
import {
  CommunicationChannelType,
  PersonStatus,
  PersonInHouseholdDto,
} from 'graphql/person/generates';
import { useCreateOrUpdateCommunicationChannel } from 'hooks/useCreateOrUpdateCommunicationChannel';
import { useSendConsent } from 'hooks/useSendConsent';
import { useUpdatePersonConsentData } from 'hooks/useUpdatePersonConsentData';
import { useUpdateLegalGuardian } from 'hooks/useUpdateLegalGuardian';
import { consentType as consentTypes, guardianSelectTypes } from 'service/model/consentType';
import { FieldType } from 'utils/fieldList';
import { checkIcon, getPersonFullName, removeEmptySpace, xl } from 'utils/util';
import { formatDate, validateConsentDate } from 'utils/validation';

import type { BasicDataConsentPayload } from 'type/basicData';
import type { CommunicationChannelSectionDataType, PersonConsentData } from 'type/person';
import { defaultConsentData, handleDataBeforeSubmit } from '../ConsentModal/handleData';
import {
  getConsentDataQueryKey,
  useGetConsentModalData,
} from '../ConsentModal/useGetConsentModalData';

import { getMonitoring } from '../../service/getMonitoring';
import { isPersonYoungerThan18 } from '../../screen/HouseholdDashboard/component/BottomCard/util';
import { ConsentTypeSelect } from './component/ConsentTypeSelect';
import './style.css';
import { LegalGuardianSelect } from './component/LegalGuardianSelect';
import { GuardianOtherPersonCombobox } from './component/GuardianOtherPersonCombobox';
import { useGetLegalGuardianData } from '../ConsentModal/useGetLegalGuardianData';
import { getHouseholdDashboardQueryKey } from '../../hooks/useGetHouseholdDashboard';

const CLEAR_FORM_TIMEOUT = 300;

interface ConsentOrLegalGuardianModalPropList {
  visible: boolean;
  person: PersonInHouseholdDto;
  isConsentFetching: boolean;
  onSubmitSuccess?: () => void;
  setIsConsentEmailSent: (bool: string) => void;
  personList: PersonInHouseholdDto[];
  onOpened?: () => void;
  onClosed?: () => void;
}

export const ConsentOrLegalGuardianModal = ({
  visible,
  person,
  isConsentFetching,
  setIsConsentEmailSent,
  onSubmitSuccess,
  onOpened,
  onClosed,
  personList,
}: ConsentOrLegalGuardianModalPropList) => {
  const { householdId } = useParams<{ householdId: string }>();
  const [currentEmailData, setCurrentEmailData] = useState<{
    id: string;
    type: CommunicationChannelType;
  } | null>(null);
  const [consentType, setConsentType] = useState<string>(consentTypes.SIGNATURE);
  const [currentEmailValue, setCurrentEmailValue] = useState('');
  const [currentBirthDate, setCurrentBirthDate] = useState('');
  const [isSignatureDisabled, setIsSignatureDisabled] = useState(false);
  const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);
  const [personStatus, setPersonStatus] = useState<PersonStatus | null>(null);
  const [guardianSelectType, setGuardianSelectType] = useState<string>(
    guardianSelectTypes.HOUSEHOLD_MEMBER,
  );
  const [guardianPersisted, setGuardianPersisted] = useState<string | undefined>();

  const queryClient = useQueryClient();
  const updatePersonConsentData = useUpdatePersonConsentData(person.id);
  const createOrUpdateCommunicationChannel = useCreateOrUpdateCommunicationChannel(person.id);
  const sendConsent = useSendConsent({
    householdId: householdId as string,
    personId: person.id,
  });
  const updateLegalGuardian = useUpdateLegalGuardian(person.id);

  const { data, isFetched: isDataFetched } = useGetConsentModalData(
    person.id,
    householdId as string,
  );

  const guardianNumberStr = data?.person.legalGuardianNumber
    ? String(data.person.legalGuardianNumber)
    : undefined;

  const guardianOptions = useMemo(
    () =>
      personList
        .filter((p) => !isPersonYoungerThan18(p.birthDate))
        .filter((p) => p.id !== person.id)
        .map((p) => ({ label: getPersonFullName(p), value: p.id })),
    [person.id, personList],
  );

  const guardianOutsideHH =
    !!guardianNumberStr && !personList.some((p) => p.id === guardianNumberStr);

  const {
    data: guardianExternalData,
    isFetched: isGuardianFetched,
    isLoading: isGuardianLoading,
  } = useGetLegalGuardianData(guardianOutsideHH ? guardianNumberStr : '');

  const externalGuardianName = guardianExternalData
    ? `${guardianExternalData.person.basicData.firstName} ${guardianExternalData.person.basicData.lastName}`
    : '';

  const isSubmitting =
    updatePersonConsentData.isPending ||
    createOrUpdateCommunicationChannel.isPending ||
    sendConsent.isPending ||
    updateLegalGuardian.isPending ||
    isSubmitInProgress;

  const {
    handleSubmit,
    reset,
    getValues,
    formState,
    setValue,
    register,
    trigger,
    setError,
    clearErrors,
  } = useForm<PersonConsentData>({
    values: {
      birthDate: currentBirthDate.length ? currentBirthDate : person?.birthDate,
      email: currentEmailValue || '',
      guardian: guardianNumberStr ?? guardianPersisted ?? null,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: defaultConsentData,
  });

  const guardianFieldValue = getValues(FieldType.guardian);

  const { errors, isValid, dirtyFields } = formState;

  const clearForm = useCallback(() => {
    setConsentType('');
    setCurrentEmailData(null);
    setCurrentEmailValue('');
    reset(defaultConsentData);
    setIsSignatureDisabled(false);
  }, [reset]);

  const setMainEmail = useCallback(() => {
    const cChannel = data?.person.communicationChannel || null;
    if (cChannel?.length) {
      const emailChannel = cChannel.find(
        ({ type }) => type === CommunicationChannelType.EMailPrivat,
      );
      if (emailChannel) {
        const { id, type, value } = emailChannel;
        setCurrentEmailData({ id, type });
        setCurrentEmailValue(value);
        return;
      }
    }

    setCurrentEmailData(null);
    setCurrentEmailValue('');
  }, [data]);

  const onChange = (value: string | undefined, fieldName: keyof PersonConsentData) => {
    const sanitized = removeEmptySpace<string | undefined>(value);
    if (getValues(fieldName) === sanitized) {
      return;
    }
    setValue(fieldName, sanitized, { shouldDirty: true, shouldValidate: true });
  };

  useEffect(() => {
    if (visible && data) {
      setPersonStatus(data.person.basicData?.status ?? null);
    }
  }, [visible, data]);

  useEffect(() => {
    if (visible && isDataFetched) {
      setMainEmail();
    }
  }, [visible, isDataFetched, setMainEmail]);

  useEffect(() => {
    setCurrentBirthDate(person?.birthDate ?? data?.person.basicData.birthDate ?? '');
  }, [person]);

  useEffect(() => {
    if (!visible || !guardianNumberStr) {
      return;
    }

    const existsInHousehold = personList.some((p) => p.id === guardianNumberStr);
    if (!existsInHousehold) {
      setGuardianSelectType(guardianSelectTypes.OTHER_PERSON);
    }

    if (existsInHousehold) {
      setGuardianSelectType(guardianSelectTypes.HOUSEHOLD_MEMBER);
      setValue(FieldType.guardian, guardianNumberStr, {
        shouldDirty: true,
        shouldValidate: true,
      });
      clearErrors(FieldType.guardian);
    } else if (isGuardianFetched && !isGuardianLoading) {
      setGuardianSelectType(guardianSelectTypes.OTHER_PERSON);
      setValue(FieldType.guardian, guardianNumberStr, {
        shouldDirty: true,
        shouldValidate: true,
      });
      clearErrors(FieldType.guardian);
    }
  }, [
    visible,
    guardianNumberStr,
    personList.length,
    setValue,
    clearErrors,
    isGuardianFetched,
    isGuardianLoading,
  ]);

  const invalidateConsentQueries = async () =>
    queryClient.invalidateQueries({ queryKey: getConsentDataQueryKey(person.id) });

  const processPayloadItem = async (
    payloadData: CommunicationChannelSectionDataType,
    personConsentData: PersonConsentData,
  ) => {
    if (payloadData?.value) {
      reset(personConsentData, { keepDirty: false });
      await createOrUpdateCommunicationChannel.mutateAsync(payloadData);
    }

    if (payloadData?.birthDate && personStatus !== 'KUNDE' && personStatus !== 'ALTKUNDE') {
      reset(personConsentData, { keepDirty: false });
      setCurrentBirthDate(payloadData.birthDate);

      await updatePersonConsentData.mutateAsync({
        ...data?.person.basicData,
        ...payloadData,
      } as BasicDataConsentPayload);
    }

    await invalidateConsentQueries();
  };

  const updateGuardianIfNeeded = async (guardian: string | null | undefined) => {
    if (!guardian) {
      return;
    }

    try {
      await updateLegalGuardian.mutateAsync({
        legalGuardianNumber: Number(guardian),
      });
    } catch (err) {
      setError(FieldType.guardian, {
        type: 'server',
        message: i18next.t(
          'sendConsent.consentOrLegalGuardianModal.legalGuardian.legalGuardianIsMinor',
        ),
      });
      getMonitoring().trackException(err as Error);
      setIsSubmitInProgress(true);
      return;
    } finally {
      await queryClient.refetchQueries({ queryKey: ['getConsentData'] });
      if (householdId) {
        await queryClient.refetchQueries({
          queryKey: getHouseholdDashboardQueryKey(householdId),
        });
      }

      await queryClient.refetchQueries({
        queryKey: ['getConsentModalData'],
      });
    }
  };

  const sendConsentIfAdult = async () => {
    if (consentType === consentTypes.EMAIL) {
      await sendConsent.mutateAsync({});
      setIsConsentEmailSent('success');
    } else {
      const callbackLink = `${document.referrer}/${appNames.advisor}${window.location.pathname}`;
      const consentResponse = await sendConsent.mutateAsync({ callbackLink });
      const link = consentResponse.data.link;
      if (link) {
        window.parent.location.replace(link);
      }
    }
  };

  const onSubmit = useCallback(
    async (personConsentData: PersonConsentData) => {
      const personIsMinor = isPersonYoungerThan18(personConsentData.birthDate);

      if (!isValid) {
        await trigger();
        return;
      }
      if (personIsMinor && personConsentData.guardian) {
        setGuardianPersisted(personConsentData.guardian);
      }

      setIsSubmitInProgress(true);
      setIsSignatureDisabled(true);

      const payload = handleDataBeforeSubmit(
        householdId as string,
        person.id,
        personConsentData,
        {
          id: currentEmailData?.id,
          type: currentEmailData?.type,
        },
        dirtyFields,
      );

      const requests = payload.map(async (payloadData) =>
        processPayloadItem(payloadData, personConsentData).catch(getMonitoring().trackException),
      );

      await Promise.all(requests);

      if (personIsMinor) {
        await updateGuardianIfNeeded(personConsentData.guardian);
      }

      if (!personIsMinor) {
        await sendConsentIfAdult()
          .catch(getMonitoring().trackException)
          .finally(async () => queryClient.refetchQueries({ queryKey: ['getConsentData'] }));
      }

      onSubmitSuccess?.();
      setIsSignatureDisabled(false);
    },
    [
      isValid,
      householdId,
      person.id,
      currentEmailData?.id,
      currentEmailData?.type,
      dirtyFields,
      personStatus,
      reset,
      createOrUpdateCommunicationChannel,
      queryClient,
      updatePersonConsentData,
      data?.person.basicData,
      sendConsent,
      setIsConsentEmailSent,
      onSubmitSuccess,
      trigger,
      consentType,
      updateLegalGuardian,
    ],
  );

  const translation = {
    note: i18next.t('sendConsent.note'),
    redirectInProgress: i18next.t('sendConsent.redirectInProgress'),
    emailIsSent: i18next.t('sendConsent.emailIsSent'),
    title: i18next.t('sendConsent.consentOrLegalGuardianModal.title'),
    subtitle: i18next.t('sendConsent.consentOrLegalGuardianModal.subtitle'),
    person: i18next.t('sendConsent.consentOrLegalGuardianModal.person'),
    birthDate: i18next.t('basicData.birthDate'),
    email: i18next.t('sendConsent.consentOrLegalGuardianModal.email'),
    obtainPrivacyPolicy: i18next.t('sendConsent.consentOrLegalGuardianModal.obtainPrivacyPolicy'),
  };

  const currentDate = formatDate(new Date());

  const resetCalendarError = () => {
    const dateInput = document.querySelector('dx-date-input') as HTMLDxDateInputElement;
    dateInput?.focusControl?.();
  };

  const onModalOpened = () => {
    setIsSubmitInProgress(false);
    const email = currentEmailValue || getValues(FieldType.email) || '';
    const guardianVal = getValues(FieldType.guardian);
    reset({
      birthDate: currentBirthDate || null,
      guardian: guardianVal,
      email,
    });
    setIsConsentEmailSent('');
    setConsentType(consentTypes.SIGNATURE);
    onOpened?.();
  };

  const onModalClosed = () => {
    onClosed?.();
    clearForm();
    setIsConsentEmailSent('');
    setIsSubmitInProgress(false);
    setGuardianSelectType(guardianSelectTypes.HOUSEHOLD_MEMBER);
    setTimeout(() => {
      resetCalendarError();
    }, CLEAR_FORM_TIMEOUT);
  };

  const isEmailSent = () => {
    const currentEmail = getValues(FieldType.email);
    if (!currentEmail || errors?.email?.message) {
      return false;
    }
  };

  const personFullName = getPersonFullName({
    firstName: person.firstName,
    lastName: person.lastName,
  });
  const isYoungerThan18 = isPersonYoungerThan18(getValues(FieldType.birthDate));
  const emailAlreadySent = isEmailSent();
  const showConsentTypeSelect = !isYoungerThan18 || !getValues(FieldType.birthDate);
  const showGuardianSelect = isYoungerThan18 && getValues(FieldType.birthDate);

  const handleBirthDateChange = useCallback(
    (value: string | undefined) => {
      onChange(value, FieldType.birthDate);
      const isMinor = isPersonYoungerThan18(value);
      if (isMinor) {
        clearErrors(FieldType.email);
        trigger(FieldType.guardian);
      } else {
        clearErrors(FieldType.guardian);
      }
    },
    [clearErrors, trigger],
  );

  const isBirthDateProvided = !!getValues(FieldType.birthDate);
  const isGuardianSelected = !!getValues(FieldType.guardian);
  const disableForMinorMissingGuardian =
    isYoungerThan18 && (!isBirthDateProvided || !isGuardianSelected);
  const isObtainDisabled =
    isSubmitting ||
    isConsentFetching ||
    !!Object.keys(errors).length ||
    disableForMinorMissingGuardian;

  useEffect(() => {
    register(FieldType.guardian, {
      required: isYoungerThan18 ? mandatoryFieldMessage : false,
    });
    if (!isYoungerThan18) {
      clearErrors(FieldType.guardian);
    }
  }, [isYoungerThan18, clearErrors]);

  const presetGuardianOption =
    guardianSelectType === guardianSelectTypes.OTHER_PERSON &&
    externalGuardianName &&
    guardianFieldValue
      ? {
          label: externalGuardianName,
          id: guardianFieldValue,
          preLabel: '',
          subLabel: '',
        }
      : '';

  const isEmailRequired = !showGuardianSelect;

  return (
    <DxModal
      visible={visible}
      label={translation.title}
      height="content"
      color="headline"
      data-testid="consent-guardian-modal"
      onModalClosed={onModalClosed}
      onModalOpened={onModalOpened}
      id="consent-guardian-modal"
    >
      <div slot="content" className="flex column" style={{ padding: 0 }}>
        <DxText
          id="gdpr-modal-subtitle"
          type="ps"
          color="gray-66"
          className="consent-subtitle"
          data-testid="gdpr-modal-subtitle"
        >
          {translation.subtitle}
        </DxText>

        <DxCard id="consent-card" data-testid="consent-card">
          <DxCardContent>
            <div className="consent-card-content">
              <DxTextInput
                size="m"
                readonly
                data-testid="personName"
                id="personName"
                className="consent-person-name"
                label={translation.person}
                value={personFullName}
              />
              <DxGrid id="contact-grid" data-testid="contact-grid" mq2="6-6" mq1="12/12">
                <DxDateInput
                  size="m"
                  data-testid="birthDate"
                  id="birthDate"
                  errormessage={errors?.birthDate?.message}
                  disabled={isSubmitting || personStatus === 'KUNDE' || personStatus === 'ALTKUNDE'}
                  required
                  label={translation.birthDate}
                  onValueChange={(e) => handleBirthDateChange(e.target.value)}
                  value={getValues(FieldType.birthDate) as string}
                  ref={
                    register(FieldType.birthDate, {
                      required: mandatoryFieldMessage,
                      validate: (value) => validateConsentDate(value, currentDate, true),
                    }).ref
                  }
                />

                <DxTextInput
                  size="m"
                  placeholder={i18next.t('general.pleaseEnter')}
                  className="pd_contact_value"
                  data-testid="email"
                  icon={checkIcon('mail')}
                  disabled={isSubmitting}
                  id="email"
                  ref={
                    register(FieldType.email, {
                      validate: validateEmail,
                      maxLength: xl,
                      required: isEmailRequired ? mandatoryFieldMessage : false,
                    }).ref
                  }
                  onValueChange={(event) => {
                    onChange(event.detail, FieldType.email);
                  }}
                  required={isEmailRequired}
                  value={getValues(FieldType.email)}
                  label={translation.email}
                  errormessage={errors?.email?.message}
                />
              </DxGrid>

              {showGuardianSelect && (
                <>
                  <LegalGuardianSelect
                    value={guardianSelectType}
                    onChange={(type) => {
                      setGuardianSelectType(type);
                      onChange(undefined, FieldType.guardian);
                    }}
                    isSubmitting={isSubmitting}
                    isConsentFetching={isConsentFetching}
                  />
                  {guardianSelectType === guardianSelectTypes.HOUSEHOLD_MEMBER && (
                    <DxDropdown
                      disabled={!guardianOptions.length || isSubmitting}
                      required
                      value={getValues(FieldType.guardian) as string}
                      label={i18next.t(
                        'sendConsent.consentOrLegalGuardianModal.legalGuardian.label',
                      )}
                      placeholder={i18next.t('general.pleaseSelect')}
                      size="m"
                      errormessage={
                        !guardianOptions.length
                          ? i18next.t(
                              'sendConsent.consentOrLegalGuardianModal.legalGuardian.noPersonsAvailable',
                            )
                          : errors?.guardian?.message
                      }
                      id="guardian-dropdown"
                      onValueChange={(e) => {
                        onChange(e.target.value as string, FieldType.guardian);
                      }}
                      data-testid="guardian-dropdown"
                    >
                      {guardianOptions.map((p) => (
                        <DxListItem
                          data-testid={`guardian-${p.label}`}
                          value={p.value}
                          label={p.label}
                          key={`key-${p.value}`}
                        />
                      ))}
                    </DxDropdown>
                  )}
                  {guardianSelectType === guardianSelectTypes.OTHER_PERSON && (
                    <GuardianOtherPersonCombobox
                      initialOption={presetGuardianOption}
                      errormessage={errors?.guardian?.message}
                      disabled={isSubmitting || (guardianOutsideHH && !isGuardianFetched)}
                      onClearErrors={() => clearErrors(FieldType.guardian)}
                      onSelect={(personId) => {
                        onChange(personId as string, FieldType.guardian);
                      }}
                      excludedIds={personList.map((p) => p.id)}
                    />
                  )}
                </>
              )}
            </div>
          </DxCardContent>
        </DxCard>

        {showConsentTypeSelect && (
          <div
            className="consent-single-select-container"
            id="consent-type-select-wrapper"
            data-testid="consent-type-select-wrapper"
          >
            <ConsentTypeSelect
              value={consentType}
              onChange={setConsentType}
              isSignatureDisabled={isSignatureDisabled}
              isSubmitting={isSubmitting}
              isConsentFetching={isConsentFetching}
              emailAlreadySent={emailAlreadySent}
            />
          </div>
        )}
      </div>

      <DxButton
        icon={checkIcon('schild')}
        disabled={isObtainDisabled}
        onClick={handleSubmit(onSubmit)}
        data-testid="obtain-privacy-policy-btn"
        id="obtain-privacy-policy-btn"
        label={translation.obtainPrivacyPolicy}
        type="primary-m"
        slot="primary-actions"
      />
    </DxModal>
  );
};
